import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

// ============================================
// Types pour les Paramètres
// ============================================

export interface Parametre {
  id: number;
  cle: string;
  valeur: string;
  description: string;
  type: 'string' | 'number' | 'boolean' | 'json';
  date_modification: string;
}

export interface BackupResponse {
  success: boolean;
  message: string;
  backup_filename: string;
  backup_path: string;
  taille: number;
}

export interface SuppressionDonneesResponse {
  success: boolean;
  message: string;
  backup_filename: string;
  deleted_counts: Record<string, number | string>;
  total_deleted: number;
}

// ============================================
// Types pour les Utilisateurs
// ============================================

export interface AccesTemporaire {
  pole: string;
  date_expiration: string;
  accorde_par?: string;
  date_accord?: string;
}

export interface UtilisateurList {
  id: number;
  username: string;
  email: string;
  nom: string;
  prenoms: string;
  role: 'super_admin' | 'admin' | 'agent' | 'agent_caisse' | 'directeur' | 'consultation' | 'surveillant' | 'formateur' | 'marketing' | 'bureau_stages' | 'invite';
  actif: boolean;
  derniere_connexion: string | null;
  date_joined: string;
  is_staff: boolean;
  is_superuser: boolean;
  poles_autorises: string[];
  poles_acces_temporaire: AccesTemporaire[];
  poles_accessibles: string[] | null;
  photo: string | null;
  photo_url: string | null;
}

export interface UtilisateurCreate {
  username: string;
  email: string;
  nom: string;
  prenoms: string;
  role: string; // 'super_admin' | 'admin' | 'agent' | 'agent_caisse' | 'directeur' | 'surveillant' | 'formateur' | 'marketing' | 'bureau_stages' | 'consultation'
  password: string;
  password2: string;
  poles_autorises?: string[];
  enseignant_id_lien?: number | null;
}

export interface EnseignantDisponibleItem {
  id: number;
  nom: string;
  prenoms: string;
  email: string;
  telephone: string;
  specialite: string;
  nom_complet: string;
  a_compte: boolean;
}

export interface UtilisateurUpdate {
  username?: string;
  email?: string;
  nom?: string;
  prenoms?: string;
  role?: string;
  actif?: boolean;
  poles_autorises?: string[];
}

// ============================================
// Types pour les Permissions
// ============================================

export interface PermissionItem {
  id: number;
  utilisateur: number;
  utilisateur_username: string;
  module: string;
  peut_lire: boolean;
  peut_ecrire: boolean;
  peut_modifier: boolean;
  peut_supprimer: boolean;
  peut_exporter: boolean;
  date_attribution: string;
}

export interface PermissionCreate {
  utilisateur: number;
  module: string;
  peut_lire?: boolean;
  peut_ecrire?: boolean;
  peut_modifier?: boolean;
  peut_supprimer?: boolean;
  peut_exporter?: boolean;
}

// ============================================
// Types pour les Logs d'Audit
// ============================================

export interface LogAuditItem {
  id: number;
  utilisateur: number | null;
  utilisateur_username: string | null;
  annee_academique: string;
  type_operation: string;
  table_concernee: string;
  id_enregistrement: number | null;
  ancienne_valeur: string;
  nouvelle_valeur: string;
  justification: string;
  ip_address: string | null;
  user_agent: string;
  date_operation: string;
}

export interface LogAuditFilters {
  utilisateur?: number;
  type_operation?: string;
  annee_academique?: string;
  table_concernee?: string;
  page?: number;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ============================================
// API
// ============================================

export const coreApi = createApi({
  reducerPath: 'coreApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['Parametres', 'Utilisateurs', 'Permissions', 'LogsAudit'],
  endpoints: (builder) => ({
    // ==========================================
    // PARAMÈTRES
    // ==========================================

    getParametres: builder.query<Parametre[], void>({
      query: () => '/parametres/',
      transformResponse: (response: any) =>
        Array.isArray(response) ? response : (response?.results ?? []),
      providesTags: ['Parametres'],
    }),

    getParametreById: builder.query<Parametre, number>({
      query: (id) => `/parametres/${id}/`,
      providesTags: ['Parametres'],
    }),

    creerParametre: builder.mutation<Parametre, Partial<Parametre>>({
      query: (parametre) => ({
        url: '/parametres/',
        method: 'POST',
        body: parametre,
      }),
      invalidatesTags: ['Parametres'],
    }),

    modifierParametre: builder.mutation<Parametre, { id: number; data: Partial<Parametre> }>({
      query: ({ id, data }) => ({
        url: `/parametres/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Parametres'],
    }),

    supprimerParametre: builder.mutation<{ success: boolean; message: string }, number>({
      query: (id) => ({
        url: `/parametres/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Parametres'],
    }),

    getParametreByKey: builder.query<{ cle: string; valeur: any }, string>({
      query: (cle) => `/parametres/by_key/?cle=${cle}`,
    }),

    // Infos identité institut (ville, chef scolarité) — utilisées dans
    // toutes les impressions officielles (listes brassées, PV, relevés…)
    // Différenciées automatiquement PN vs BZ selon le déploiement.
    // Verrous appliqués à la page « Association Copies » : force les
    // toggles session/parité pour éviter les erreurs des agents.
    getAssocVerrous: builder.query<{
      session_mode: '' | 'NORMAL' | 'RATTRAPAGE';
      semestre_parity: '' | 'impair' | 'pair';
    }, void>({
      query: () => '/parametres/assoc-verrous/',
      // Cache court : l'admin peut changer les verrous et l'agent doit voir vite.
      keepUnusedDataFor: 60,
    }),

    // Verrous appliqués à la page « Génération relevés correction V2 »
    // (composant `/ocr/generation-correction-v2`). Même logique que assocVerrous
    // — force type_examen et parité semestre pour éviter les erreurs des agents
    // pendant une session en cours.
    getGenerationVerrous: builder.query<{
      type_examen: '' | 'normal' | 'rattrapage';
      semestre_parity: '' | 'impair' | 'pair';
    }, void>({
      query: () => '/parametres/generation-verrous/',
      keepUnusedDataFor: 60,
    }),

    getInfoInstitut: builder.query<{
      deployment: 'pn' | 'bz' | string;
      ville: string;
      campus: string;
      chef_scolarite: string;
      chef_scolarite_titre: string;
      telephone: string;
      email: string;
      adresse_rue: string;
      numero_agrement: string;
      site_web: string;
    }, void>({
      query: () => '/parametres/info-institut/',
      // Cache long (les infos changent très rarement)
      keepUnusedDataFor: 3600,
    }),

    // ==========================================
    // UTILISATEURS
    // ==========================================

    getUtilisateurs: builder.query<UtilisateurList[], void>({
      query: () => '/utilisateurs/',
      transformResponse: (response: any) =>
        Array.isArray(response) ? response : (response?.results ?? []),
      providesTags: ['Utilisateurs'],
    }),

    creerUtilisateur: builder.mutation<UtilisateurList & { generated_password?: string }, UtilisateurCreate>({
      query: (data) => ({
        url: '/utilisateurs/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Utilisateurs'],
    }),

    // Liste des enseignants pour création d'un compte formateur (SearchableSelect)
    enseignantsDisponiblesCompte: builder.query<{
      results: EnseignantDisponibleItem[];
      total: number;
    }, { search?: string } | void>({
      query: (params) => ({
        url: '/enseignants/disponibles-compte/',
        params: params ?? undefined,
      }),
    }),

    modifierUtilisateur: builder.mutation<UtilisateurList, { id: number; data: UtilisateurUpdate }>({
      query: ({ id, data }) => ({
        url: `/utilisateurs/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Utilisateurs'],
    }),

    supprimerUtilisateur: builder.mutation<void, number>({
      query: (id) => ({
        url: `/utilisateurs/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Utilisateurs'],
    }),

    updateProfile: builder.mutation<{ success: boolean; user: UtilisateurList }, { nom?: string; prenoms?: string; email?: string }>({
      query: (data) => ({ url: '/utilisateurs/update_profile/', method: 'PATCH', body: data }),
      invalidatesTags: ['Utilisateurs'],
    }),

    updatePhoto: builder.mutation<{ success: boolean; photo_url: string; user: UtilisateurList }, FormData>({
      query: (formData) => ({ url: '/utilisateurs/update_photo/', method: 'POST', body: formData }),
      invalidatesTags: ['Utilisateurs'],
    }),

    deletePhoto: builder.mutation<{ success: boolean }, void>({
      query: () => ({ url: '/utilisateurs/delete_photo/', method: 'DELETE' }),
      invalidatesTags: ['Utilisateurs'],
    }),

    changerMotDePasse: builder.mutation<
      { success: boolean; message: string } | { error: string },
      { ancien_mot_de_passe: string; nouveau_mot_de_passe: string }
    >({
      query: (body) => ({
        url: '/utilisateurs/changer-mot-de-passe/',
        method: 'POST',
        body,
      }),
    }),

    affecterPolesUtilisateur: builder.mutation<
      { success: boolean; poles_autorises: string[]; message: string },
      { id: number; poles: string[] }
    >({
      query: ({ id, poles }) => ({
        url: `/utilisateurs/${id}/affecter_poles/`,
        method: 'POST',
        body: { poles },
      }),
      invalidatesTags: ['Utilisateurs'],
    }),

    accorderAccesTemporaire: builder.mutation<
      { success: boolean; poles_acces_temporaire: AccesTemporaire[]; message: string },
      { id: number; pole: string; date_expiration: string }
    >({
      query: ({ id, pole, date_expiration }) => ({
        url: `/utilisateurs/${id}/accorder_acces_temporaire/`,
        method: 'POST',
        body: { pole, date_expiration },
      }),
      invalidatesTags: ['Utilisateurs'],
    }),

    revoquerAccesTemporaire: builder.mutation<
      { success: boolean; message: string },
      { id: number; pole: string }
    >({
      query: ({ id, pole }) => ({
        url: `/utilisateurs/${id}/revoquer_acces_temporaire/`,
        method: 'POST',
        body: { pole },
      }),
      invalidatesTags: ['Utilisateurs'],
    }),

    // ==========================================
    // PERMISSIONS
    // ==========================================

    getPermissions: builder.query<PermissionItem[], { utilisateur?: number } | void>({
      query: (params) => ({
        url: '/permissions/',
        params: params ?? undefined,
      }),
      transformResponse: (response: any) =>
        Array.isArray(response) ? response : (response?.results ?? []),
      providesTags: ['Permissions'],
    }),

    creerPermission: builder.mutation<PermissionItem, PermissionCreate>({
      query: (data) => ({
        url: '/permissions/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Permissions'],
    }),

    modifierPermission: builder.mutation<PermissionItem, { id: number; data: Partial<PermissionCreate> }>({
      query: ({ id, data }) => ({
        url: `/permissions/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Permissions'],
    }),

    supprimerPermission: builder.mutation<void, number>({
      query: (id) => ({
        url: `/permissions/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Permissions'],
    }),

    // ==========================================
    // LOGS D'AUDIT (lecture seule)
    // ==========================================

    getLogsAudit: builder.query<PaginatedResponse<LogAuditItem>, LogAuditFilters | void>({
      query: (filters) => ({
        url: '/logs-audit/',
        params: filters ?? undefined,
      }),
      providesTags: ['LogsAudit'],
    }),

    // ==========================================
    // GESTION DES DONNÉES (ADMIN UNIQUEMENT)
    // ==========================================

    creerBackup: builder.mutation<BackupResponse, void>({
      query: () => ({
        url: '/parametres/creer_backup/',
        method: 'POST',
      }),
    }),

    supprimerToutesDonnees: builder.mutation<SuppressionDonneesResponse, { confirmation: string }>({
      query: (data) => ({
        url: '/parametres/supprimer_toutes_donnees/',
        method: 'POST',
        body: data,
      }),
    }),

    resetPassword: builder.mutation<{ success: boolean; message: string }, { id: number; new_password: string }>({
      query: ({ id, new_password }) => ({
        url: `/utilisateurs/${id}/reset_password/`,
        method: 'POST',
        body: { new_password },
      }),
    }),

    toggleActifUtilisateur: builder.mutation<{ success: boolean; actif: boolean; message: string }, number>({
      query: (id) => ({
        url: `/utilisateurs/${id}/toggle_actif/`,
        method: 'POST',
      }),
      invalidatesTags: ['Utilisateurs'],
    }),

    appliquerProfilPermissions: builder.mutation<{ success: boolean; message: string }, { utilisateur_id: number; profil: string }>({
      query: (data) => ({
        url: '/utilisateurs/appliquer_profil_permissions/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Permissions', 'Utilisateurs'],
    }),

    listerBackups: builder.query<{
      backups: Array<{ filename: string; taille: number; date_creation: string }>;
      total: number;
      repertoire: string;
    }, void>({
      query: () => '/parametres/lister_backups/',
    }),

    statistiquesDonnees: builder.query<{
      schema: string;
      compteurs: Record<string, { label: string; count: number | null }>;
    }, void>({
      query: () => '/parametres/statistiques_donnees/',
    }),

    activerMaintenance: builder.mutation<{ success: boolean; maintenance: boolean; message: string }, { activer: boolean; message?: string }>({
      query: (data) => ({
        url: '/utilisateurs/activer_maintenance/',
        method: 'POST',
        body: data,
      }),
    }),

    getEtatMaintenance: builder.query<{ maintenance: boolean; message: string }, void>({
      query: () => '/utilisateurs/etat_maintenance/',
    }),

    deconnecterTous: builder.mutation<{ success: boolean; nb_deconnectes: number; message: string }, void>({
      query: () => ({
        url: '/utilisateurs/deconnecter_tous/',
        method: 'POST',
      }),
    }),

    // Fix 2026-09-09 : déconnexion ciblée d'un utilisateur unique
    deconnecterUn: builder.mutation<{
      success: boolean; user_id: number; nom_complet: string;
      force_logout_at: string; message: string;
    }, number>({
      query: (userId) => ({
        url: `/utilisateurs/${userId}/deconnecter/`,
        method: 'POST',
      }),
    }),

    getSurveillance: builder.query<{
      en_ligne: Array<{ id: number; username: string; nom: string; role: string; derniere_connexion: string | null }>;
      hors_ligne: Array<{ id: number; username: string; nom: string; role: string; derniere_connexion: string | null }>;
      nb_en_ligne: number;
      nb_total: number;
      historique_connexions: Array<{ utilisateur: string; nom: string; date: string; ip: string | null; user_agent: string }>;
      activite_par_utilisateur: Array<{ username: string; nom: string; total: number; par_type: Record<string, number> }>;
      activite_par_jour: Array<{ date: string; count: number }>;
      stats: { total_operations_7j: number; total_connexions_7j: number; nb_utilisateurs_actifs: number };
    }, void>({
      query: () => '/utilisateurs/surveillance/',
    }),

    // ── Audit trail HTTP (Phase 3 sécurité — 2026-08-27) ────────────────
    auditActiviteListe: builder.query<AuditActiviteListe, AuditActiviteParams>({
      query: (params = {}) => ({ url: '/audit-activite/', params }),
    }),
    auditActiviteStats: builder.query<AuditActiviteStats, { depuis?: string; jusqu?: string }>({
      query: (params = {}) => ({ url: '/audit-activite/stats/', params }),
    }),
    auditActiviteUser: builder.query<AuditActiviteUser, { user_id: number; depuis?: string; limit?: number }>({
      query: ({ user_id, ...params }) => ({ url: `/audit-activite/user/${user_id}/`, params }),
    }),

    // ── Journal des notes (trigger PostgreSQL — 2026-09-11) ─────────────
    journalNotes: builder.query<JournalNotesListe, JournalNotesParams>({
      query: (params = {}) => ({ url: '/journal-notes/', params }),
    }),
  }),
});

// ── Types Audit Activite ──────────────────────────────────────────────────
export interface AuditActiviteLigne {
  id: number;
  date: string;
  user_id: number | null;
  user_nom: string;
  user_username: string;
  user_role: string;
  method: string;
  path: string;
  query: string;
  view_name: string;
  statut_http: number | null;
  duree_ms: number | null;
  ip: string;
  user_agent: string;
  referer: string;
  payload_resume: string;
  tags: string;
}
export interface AuditActiviteParams {
  depuis?: string;
  jusqu?: string;
  user_id?: number;
  method?: string;
  path?: string;
  tag?: string;
  statut_min?: number;
  statut_max?: number;
  limit?: number;
}
export interface AuditActiviteListe {
  total: number;
  nb_retour: number;
  items: AuditActiviteLigne[];
}
export interface AuditActiviteTopUser {
  user_id: number;
  nom_complet: string;
  username: string;
  role: string;
  nb: number;
}
export interface AuditActiviteStats {
  total: number;
  top_users: AuditActiviteTopUser[];
  top_paths: Array<{ path: string; nb: number }>;
  par_method: Array<{ method: string; nb: number }>;
  par_tag: { nuit: number; destructif: number; sensible: number; mobile: number };
  par_heure: Array<{ heure: number; nb: number }>;
  alertes: {
    'accès_sensible_nuit': number;
    'destructif_sensible': number;
    nb_5xx: number;
    nb_4xx: number;
  };
}
export interface AuditActiviteUser {
  user: { id: number; nom_complet: string; username: string; role: string } | null;
  total: number;
  nb_retour: number;
  items: AuditActiviteLigne[];
  top_paths: Array<{ path: string; nb: number }>;
  ips: Array<{ ip: string; nb: number }>;
  user_agents: Array<{ ua: string; nb: number }>;
}

// ── Types Journal des notes ───────────────────────────────────────────────
export interface JournalNoteChangement {
  champ: string;
  avant: string | number | boolean | null;
  apres: string | number | boolean | null;
}
export interface JournalNoteLigne {
  id: number;
  date: string;
  table: 'devoir' | 'examen';
  operation: 'I' | 'U' | 'D';
  note_id: number | null;
  semestre: string | null;
  est_rattrapage: boolean | null;
  etudiant: { id: number | null; matricule: string; nom: string; classe: string };
  matiere: { id: number | null; code: string; nom: string };
  changements: JournalNoteChangement[];
  utilisateur: { id: number; username: string; nom: string; role: string } | null;
  utilisateur_texte: string;
  adresse_ip: string;
  chemin: string;
  origine: string;
  utilisateur_db: string;
}
export interface JournalNotesParams {
  depuis?: string;
  jusqu?: string;
  matricule?: string;
  matiere?: string;
  user_id?: number;
  table?: string;
  operation?: string;
  limit?: number;
}
export interface JournalNotesListe {
  total: number;
  nb_retour: number;
  items: JournalNoteLigne[];
}

export const {
  // Paramètres
  useGetParametresQuery,
  useGetParametreByIdQuery,
  useCreerParametreMutation,
  useModifierParametreMutation,
  useSupprimerParametreMutation,
  useGetParametreByKeyQuery,
  useGetInfoInstitutQuery,
  useGetAssocVerrousQuery,
  useGetGenerationVerrousQuery,
  // Utilisateurs
  useGetUtilisateursQuery,
  useCreerUtilisateurMutation,
  useEnseignantsDisponiblesCompteQuery,
  useModifierUtilisateurMutation,
  useSupprimerUtilisateurMutation,
  useUpdateProfileMutation,
  useUpdatePhotoMutation,
  useChangerMotDePasseMutation,
  useDeletePhotoMutation,
  useAffecterPolesUtilisateurMutation,
  useAccorderAccesTemporaireMutation,
  useRevoquerAccesTemporaireMutation,
  // Permissions
  useGetPermissionsQuery,
  useCreerPermissionMutation,
  useModifierPermissionMutation,
  useSupprimerPermissionMutation,
  // Logs d'audit
  useGetLogsAuditQuery,
  // Gestion des données
  useCreerBackupMutation,
  useSupprimerToutesDonneesMutation,
  useResetPasswordMutation,
  useToggleActifUtilisateurMutation,
  useAppliquerProfilPermissionsMutation,
  useListerBackupsQuery,
  useStatistiquesDonneesQuery,
  useGetSurveillanceQuery,
  useActiverMaintenanceMutation,
  useGetEtatMaintenanceQuery,
  useDeconnecterTousMutation,
  useDeconnecterUnMutation,
  // Audit trail HTTP
  useAuditActiviteListeQuery,
  useAuditActiviteStatsQuery,
  useAuditActiviteUserQuery,
  // Journal des notes
  useJournalNotesQuery,
} = coreApi;
